'use es6';

import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import { formatPattern } from 'react-router';
import { push, replace } from 'react-router-redux';
import { Map } from 'immutable';
import I18n from 'I18n';
import Raven from 'Raven';
import ActionTypes from '../constants/ActionTypes';
import CheckGoodUsageErrorTypes from '../constants/CheckGoodUsageErrorTypes';
import ContentRoutes from 'ContentUtils/Routes';
import CosIndexClient from '../data/CosIndexClient';
import GoodFiltersAndPage from '../constants/GoodFiltersAndPage';
import GoodSubTypePythonFieldNames from '../constants/GoodSubTypePythonFieldNames';
import ManagePurchasesColumns from '../constants/ManagePurchasesColumns';
import DetailsPanelItems from '../constants/DetailsPanelItems';
import PaymentMethods from '../constants/PaymentMethods';
import PayPalPurchaseErrors, { isProviderError } from '../constants/PayPalPurchaseErrors';
import StripePurchaseErrors from '../constants/StripePurchaseErrors';
import InitialFilterParams from '../constants/InitialFilterParams';
import ProductMarketplaceClient from '../data/ProductMarketplaceClient';
import RequestStatusTypes from '../constants/RequestStatusTypes';
import SortOptions from '../constants/SortOptions';
import UninstallModalTypes from '../constants/UninstallModalTypes';
import * as UrlPathParams from '../constants/UrlPathParams';
import GoodSubTypes from '../constants/GoodSubTypes';
import { ITEMS_PER_ROW } from '../constants/Limits';
import { CANCELED } from 'marketplace-lib/constants/RefundStatusTypes';
import { getEnglishTextFieldFromGoodTextObj, getPollIntervalInMilliseconds, getSecondsElapsedBeforeNextPolling, getUrlWithNonInitialFiltersAndPageShown, hasFifteenMinutesElapsed, isCustomer, isTrialPortal, mergeGoodFiltersAndPage } from '../utils/utils';
import { generatePurchasedGoodsKeyForChildGood, getChildDestinationId, getPackChildrenInUseDestinationIds, getChildTemplatePath, getDestinationIdForGoodNotInPack, getPackChildrenDestinationIds, getPackChildrenInUse, getTemplatePathForGoodNotInPack } from '../utils/managePurchases/utils';
import { getHasSitePagesAccess, getIsCustomer } from '../selectors/auth';
import { usageTrackerActions } from 'usage-tracker-redux';
import { EventTypes, Screens } from '../lib/usage/constants';
import { maybeEarlyRequest } from '../utils/earlyRequests';

function _getGoodDetails(error) {
  if (error) {
    return {
      type: ActionTypes.GOOD_DETAILS_REQUESTED,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.GOOD_DETAILS_REQUESTED
  };
}

function goodsRequestedOnPageLoad(error) {
  if (error) {
    return {
      type: ActionTypes.GOODS_REQUESTED_ON_PAGE_LOAD,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.GOODS_REQUESTED_ON_PAGE_LOAD
  };
}

function _purchaseGoodWithPaypal(error) {
  if (error) {
    return {
      type: ActionTypes.PURCHASED_WITH_PAYPAL,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.PURCHASED_WITH_PAYPAL
  };
}

function _purchaseGoodWithStripe(goodId, error) {
  if (error) {
    return {
      type: ActionTypes.PURCHASED_WITH_STRIPE,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.PURCHASED_WITH_STRIPE,
    payload: goodId
  };
}

function paypalPurchaseCompleted(goodId) {
  return {
    type: ActionTypes.PAYPAL_PURCHASE_COMPLETED,
    payload: {
      goodId: goodId
    }
  };
}

function _updateGoodFiltersOrPage(newParams, screen) {
  var params = Map(newParams);
  return {
    type: ActionTypes.FILTERS_OR_PAGE_UPDATED,
    payload: params,
    meta: {
      screen: screen
    }
  };
}

export function onClickPurchaseErrorModalClose() {
  return {
    type: ActionTypes.DISMISSED_PURCHASE_ERROR
  };
}

function receiveGoodDetails(response) {
  return {
    type: ActionTypes.GOOD_DETAILS_RECEIVED,
    payload: response
  };
}

function receiveGoods(response) {
  return {
    type: ActionTypes.GOODS_RECEIVED,
    payload: response
  };
}

function stripePurchaseCompleted() {
  return {
    type: ActionTypes.STRIPE_PURCHASE_COMPLETED
  };
}

function updateGoodFiltersOrPage(filterAndPageParams, dispatch, getState, screen) {
  dispatch(_updateGoodFiltersOrPage(filterAndPageParams, screen));
  var updatedFilteringAndPageParams = mergeGoodFiltersAndPage(getState().directoryData.get('goodFiltersAndPage'), filterAndPageParams);
  dispatch(push(getUrlWithNonInitialFiltersAndPageShown(updatedFilteringAndPageParams)));
}

export function goToAllProducts() {
  return function (dispatch) {
    dispatch(push('products'));
  };
}
export function goToManagePurchases() {
  return function (dispatch) {
    dispatch(push('/manage-purchases'));
  };
}
export function getGoodDetails(providerUrlToken, goodNameUrlToken, previewMode, isCustomerBool) {
  return function (dispatch) {
    dispatch(_getGoodDetails());
    ProductMarketplaceClient.getGoodDetails(providerUrlToken, goodNameUrlToken, previewMode, isCustomerBool).then(function (response) {
      dispatch(receiveGoodDetails(response));
    }).catch(function (error) {
      Raven.captureMessage('ERROR_API_GOOD_DETAILS', {
        level: 'error',
        extra: {
          error: error,
          providerUrlToken: providerUrlToken,
          goodNameUrlToken: goodNameUrlToken
        }
      });

      if (error.status === 404) {
        dispatch(replace('/products'));
      } else {
        dispatch(_getGoodDetails(new Error(error)));
      }
    });
  };
}
export function hideAutocompleteMenu() {
  return {
    type: ActionTypes.HIDE_AUTOCOMPLETE_MENU
  };
}

function updateAutocompleteMenu(result) {
  return {
    type: ActionTypes.UPDATE_AUTOCOMPLETE_MENU,
    payload: result
  };
}

export function fetchForAutocomplete(searchTerm) {
  return function (dispatch, getState) {
    var _currentFilters$merge;

    if (!searchTerm) {
      dispatch(updateAutocompleteMenu([]));
      return;
    }

    var currentFilters = getState().directoryData.get('goodFiltersAndPage');
    ProductMarketplaceClient.getGoods(currentFilters.merge((_currentFilters$merge = {}, _defineProperty(_currentFilters$merge, GoodFiltersAndPage.SEARCH_TERM, searchTerm), _defineProperty(_currentFilters$merge, GoodFiltersAndPage.PAGE, 0), _defineProperty(_currentFilters$merge, GoodFiltersAndPage.SORT_BY, SortOptions.MOST_DOWNLOADED), _defineProperty(_currentFilters$merge, GoodFiltersAndPage.LIMIT, ITEMS_PER_ROW), _currentFilters$merge)), isCustomer(getState().auth.portal)).then(function (resp) {
      dispatch(updateAutocompleteMenu(resp.result));
    }, function () {// TODO: decide how to handle errors during update
    }).done();
  };
}
export function getGoodsWithFilterAndPageParamsFromUrl(filterAndPageParams, navigatedToDirectoryPage, earlyRequestName) {
  return function (dispatch, getState) {
    var state = getState();
    var mergedFilterParams = InitialFilterParams.merge(filterAndPageParams);

    if (navigatedToDirectoryPage) {
      dispatch(goodsRequestedOnPageLoad());
    }

    dispatch(_updateGoodFiltersOrPage(mergedFilterParams, null));
    maybeEarlyRequest(earlyRequestName, function () {
      return ProductMarketplaceClient.getGoods(mergedFilterParams, getIsCustomer(state));
    }).then(function (response) {
      dispatch(receiveGoods(response));
    }, function (error) {
      if (navigatedToDirectoryPage) {
        dispatch(goodsRequestedOnPageLoad(new Error(error)));
      } // TODO: decide how to handle errors during update

    }).done();
  };
}
export function purchaseGoodWithPaypal(data) {
  return function (dispatch) {
    dispatch(_purchaseGoodWithPaypal());
    ProductMarketplaceClient.purchaseGood(PaymentMethods.PAYPAL, data).then(function (resp) {
      if (resp.redirectUrl) {
        window.location = resp.redirectUrl;
      } else {
        dispatch(_purchaseGoodWithPaypal(new Error(PayPalPurchaseErrors.PAYPAL_PURCHASE_FAILED)));
        Raven.captureMessage('missing paypal redirect-url');
      }
    }, function (error) {
      var errorMsg = PayPalPurchaseErrors.PAYPAL_PURCHASE_FAILED;

      try {
        var respText = error.responseJSON.message;

        if (respText && isProviderError(respText)) {
          errorMsg = PayPalPurchaseErrors.PROVIDER_ERROR;
        }
      } catch (e) {
        Raven.captureMessage('error parsing paypal purchase error', {
          paypalError: e
        });
      }

      dispatch(_purchaseGoodWithPaypal(new Error(errorMsg)));
    }).done();
  };
}
export function purchaseGoodWithStripe(data) {
  return function (dispatch) {
    dispatch(_purchaseGoodWithStripe(data.goodId));
    ProductMarketplaceClient.purchaseGood(PaymentMethods.STRIPE, data).then(function () {
      dispatch(stripePurchaseCompleted());
      dispatch(push('manage-purchases'));
    }, function (error) {
      var errorMsg = 'unknown error';

      try {
        var respText = JSON.parse(error.xhr.responseText);

        if (respText && respText.message) {
          errorMsg = respText.message;
        }
      } catch (e) {
        Raven.captureMessage("error parsing stripe purchase error: " + e);
      }

      dispatch(_purchaseGoodWithStripe(null, new Error(errorMsg)));
    }).done();
  };
}

function receivePurchasedGoods(goodsInResponse) {
  var purchasedGoodsTotal = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  var purchasedGoodsDisplayOrder = [];
  var purchasedGoods = {};
  goodsInResponse.forEach(function (good) {
    var _purchasedGoods$goodI;

    var goodId = good.goodDetails.goodInfo.goodId.toString();
    purchasedGoodsDisplayOrder.push(goodId);
    var packChildrenDisplayOrder = [];

    if (good.goodDetails.children) {
      good.goodDetails.children.forEach(function (child) {
        var _purchasedGoods$gener;

        var childGoodId = child.goodId.toString();
        packChildrenDisplayOrder.push(childGoodId);
        purchasedGoods[generatePurchasedGoodsKeyForChildGood({
          childId: childGoodId,
          parentId: goodId
        })] = (_purchasedGoods$gener = {
          goodId: childGoodId,
          parentId: goodId,
          urlToken: child.urlToken,
          goodVersionId: child.goodVersionId,
          templatePath: getChildTemplatePath(good.deliverDetails.goodDeliverInfo, child),
          destinationId: getChildDestinationId(good.deliverDetails.goodDeliverInfo, child)
        }, _defineProperty(_purchasedGoods$gener, ManagePurchasesColumns.IMAGE, child.images.MARKETING_IMAGE_SMALL), _defineProperty(_purchasedGoods$gener, ManagePurchasesColumns.PRODUCT_NAME, getEnglishTextFieldFromGoodTextObj('NAME', child.texts)), _defineProperty(_purchasedGoods$gener, ManagePurchasesColumns.PRODUCT_TYPE, child.subType), _defineProperty(_purchasedGoods$gener, ManagePurchasesColumns.PURCHASE_DATE, good.transaction.createdAt), _defineProperty(_purchasedGoods$gener, DetailsPanelItems.CREATED_BY, good.goodDetails.providerInfo.company), _defineProperty(_purchasedGoods$gener, DetailsPanelItems.TRANSACTION_ID, good.transaction.id), _defineProperty(_purchasedGoods$gener, DetailsPanelItems.DOCUMENTATION_LINK, child.documentationLink), _defineProperty(_purchasedGoods$gener, DetailsPanelItems.EXAMPLE_LINK, child.exampleLink), _defineProperty(_purchasedGoods$gener, DetailsPanelItems.PROVIDER_WEBSITE, good.goodDetails.providerInfo.website), _defineProperty(_purchasedGoods$gener, DetailsPanelItems.PROVIDER_EMAIL, good.goodDetails.providerInfo.supportEmail || good.goodDetails.providerInfo.contactEmail), _defineProperty(_purchasedGoods$gener, DetailsPanelItems.REVIEW, child.review), _purchasedGoods$gener);
      });
    }

    purchasedGoods[goodId] = (_purchasedGoods$goodI = {
      goodId: goodId,
      urlToken: good.goodDetails.goodInfo.urlToken,
      deliveryStatus: good.deliverDetails.status,
      deliveryErrorMessage: good.deliverDetails.error,
      templatePath: getTemplatePathForGoodNotInPack(good.deliverDetails.goodDeliverInfo),
      destinationId: getDestinationIdForGoodNotInPack(good.deliverDetails.goodDeliverInfo),
      newVersionAvailable: good.goodDetails.newVersionAvailable
    }, _defineProperty(_purchasedGoods$goodI, ManagePurchasesColumns.IMAGE, good.goodDetails.goodInfo.goodImages.MARKETING_IMAGE_SMALL), _defineProperty(_purchasedGoods$goodI, ManagePurchasesColumns.PRODUCT_NAME, getEnglishTextFieldFromGoodTextObj('NAME', good.goodDetails.goodInfo.goodText)), _defineProperty(_purchasedGoods$goodI, ManagePurchasesColumns.PRODUCT_TYPE, good.goodDetails.goodInfo.subType), _defineProperty(_purchasedGoods$goodI, ManagePurchasesColumns.PURCHASE_DATE, good.transaction.createdAt), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.BUYER_EMAIL, good.transaction.buyerEmail), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.CREATED_BY, good.goodDetails.providerInfo.company), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.IS_FREE, good.goodDetails.goodInfo.free), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.PRICE, good.transaction.price), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.PAYMENT_TYPE, good.transaction.paymentType), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.TRANSACTION_ID, good.transaction.id), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.DOCUMENTATION_LINK, good.goodDetails.goodInfo.documentationLink), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.EXAMPLE_LINK, good.goodDetails.goodInfo.exampleLink), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.PROVIDER_WEBSITE, good.goodDetails.providerInfo.website), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.PROVIDER_EMAIL, good.goodDetails.providerInfo.supportEmail || good.goodDetails.providerInfo.contactEmail), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.REFUND_REQUESTED_DATE, good.transaction.refundRequestedDate), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.REFUND, good.transaction.refund), _defineProperty(_purchasedGoods$goodI, DetailsPanelItems.REVIEW, good.review), _defineProperty(_purchasedGoods$goodI, "packChildrenDisplayOrder", packChildrenDisplayOrder), _defineProperty(_purchasedGoods$goodI, "totalChildren", good.goodDetails.totalChildren), _defineProperty(_purchasedGoods$goodI, "showChildren", false), _purchasedGoods$goodI);
  });
  return {
    type: ActionTypes.PURCHASED_GOODS_RECEIVED,
    payload: {
      purchasedGoodsDisplayOrder: purchasedGoodsDisplayOrder,
      purchasedGoodsTotal: purchasedGoodsTotal,
      purchasedGoods: purchasedGoods
    }
  };
}

function _getPurchasedGoods(error) {
  if (error) {
    return {
      type: ActionTypes.PURCHASED_GOODS_REQUESTED,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.PURCHASED_GOODS_REQUESTED
  };
}

function trackPurchaseWithPaypal(auth, paymentAmount, subType) {
  return usageTrackerActions.track(EventTypes.PURCHASED_GOOD, {
    screen: Screens.DETAILS,
    value: paymentAmount,
    value_str: subType,
    payment_type: 'paypal',
    is_trial: isCustomer(auth.portal) && isTrialPortal(auth.portal)
  });
}

export function handleRedirectFromPaypal(_ref) {
  var goodId = _ref.goodId,
      _ref$paymentAmount = _ref.paymentAmount,
      paymentAmount = _ref$paymentAmount === void 0 ? 0 : _ref$paymentAmount,
      _ref$subType = _ref.subType,
      subType = _ref$subType === void 0 ? '' : _ref$subType;
  return function (dispatch, getState) {
    dispatch(trackPurchaseWithPaypal(getState().auth, Number(paymentAmount), subType));
    dispatch(push('/manage-purchases'));
    dispatch(paypalPurchaseCompleted(goodId));
  };
}
export function triggerPurchaseErrorModalOpening(errorMsg) {
  return function (dispatch) {
    if (errorMsg === StripePurchaseErrors.STRIPE_CHECKOUT_FAILED) {
      dispatch(_purchaseGoodWithStripe(null, new Error(errorMsg)));
    }
  };
}
export function updatePage(page) {
  return function (dispatch, getState) {
    updateGoodFiltersOrPage({
      page: page
    }, dispatch, getState);
  };
}
export function updateGoodFilters(newFilteringParams) {
  return function (dispatch, getState) {
    updateGoodFiltersOrPage(newFilteringParams, dispatch, getState);
  };
}
export function fetchMatchingSearchResults(searchTermAndResetFilterParams, screen) {
  return function (dispatch, getState) {
    dispatch(hideAutocompleteMenu());
    updateGoodFiltersOrPage(searchTermAndResetFilterParams, dispatch, getState, screen);
  };
}
export function goToGoodSearchOption(goodUrlToken, screen) {
  return function (dispatch) {
    dispatch(hideAutocompleteMenu());
    dispatch(push("/" + UrlPathParams.DIRECTORY_PAGE_PATH + "/" + goodUrlToken));
    dispatch(usageTrackerActions.track(EventTypes.SELECTED_SEARCH_OPTION, {
      screen: screen,
      value_str: goodUrlToken
    }));
  };
}
export function getPurchasedGoods() {
  return function (dispatch) {
    dispatch(_getPurchasedGoods());
    ProductMarketplaceClient.getPurchasedGoods().then(function (goods) {
      dispatch(receivePurchasedGoods(goods));
    }, function (error) {
      dispatch(_getPurchasedGoods(new Error(error)));
    }).done();
  };
}
export function getPaginatedPurchasedGoods(_ref2) {
  var offset = _ref2.offset,
      limit = _ref2.limit;
  return function (dispatch) {
    dispatch(_getPurchasedGoods());
    ProductMarketplaceClient.getPaginatedPurchasedGoods({
      offset: offset,
      limit: limit
    }).then(function (_ref3) {
      var goods = _ref3.result,
          total = _ref3.total;
      dispatch(receivePurchasedGoods(goods, total));
    }, function (error) {
      dispatch(_getPurchasedGoods(new Error(error)));
    }).done();
  };
}

function _submitReview(error) {
  if (error) {
    return {
      type: ActionTypes.REVIEW_SUBMITTED,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.REVIEW_SUBMITTED
  };
}

export function closePaypalPurchaseDelayErrorAlert() {
  return {
    type: ActionTypes.PAYPAL_DELAY_ERROR_ALERT_DISMISSED
  };
}
export function closeReviewModal() {
  return {
    type: ActionTypes.DISMISSED_REVIEW_MODAL
  };
}
export function closeCreateNewAssetModal() {
  return {
    type: ActionTypes.CREATE_NEW_ASSET_MODAL_DISMISSED
  };
}
export function closeCreateWebpageModal() {
  return {
    type: ActionTypes.CREATE_WEBPAGE_MODAL_DISMISSED
  };
}
export function closeCheckGoodUsageErrorAlert(errorId) {
  return {
    type: ActionTypes.CHECK_GOOD_USAGE_ERROR_ALERT_DISMISSED,
    payload: {
      errorId: errorId
    }
  };
}
export function closePurchasedGoodActionsMenu() {
  return {
    type: ActionTypes.PURCHASE_ACTIONS_MENU_CLOSED
  };
}
export function closePurchaseDetails() {
  return {
    type: ActionTypes.PURCHASE_DETAILS_CLOSED
  };
}
export function closeReinstallationErrorAlert() {
  return {
    type: ActionTypes.DISMISSED_REINSTALLATION_ERROR_ALERT
  };
}
export function openPurchasedGoodActionsMenu(purchasedGoodsKey) {
  return {
    type: ActionTypes.PURCHASE_ACTIONS_MENU_OPENED,
    payload: {
      purchasedGoodsKey: purchasedGoodsKey
    }
  };
}
export function openPurchaseDetails(purchasedGoodsKey) {
  return {
    type: ActionTypes.PURCHASE_DETAILS_OPENED,
    payload: {
      purchasedGoodsKey: purchasedGoodsKey
    }
  };
}
export function triggerReviewModal(goodId, goodName, transactionId, parentId) {
  return {
    type: ActionTypes.REVIEW_MODAL_OPENED,
    payload: {
      goodId: goodId,
      goodName: goodName,
      transactionId: transactionId,
      parentId: parentId
    }
  };
}
export function openCreateWebpageModal() {
  return {
    type: ActionTypes.CREATE_WEBPAGE_MODAL_OPENED
  };
}
export function openCreateNewAssetModal(good) {
  return {
    type: ActionTypes.CREATE_NEW_ASSET_MODAL_OPENED,
    payload: {
      good: good
    }
  };
}

function submitReviewFromDetailsSuccess(goodId, review) {
  return {
    type: ActionTypes.REVIEW_SUBMITTED_FROM_DETAILS_SUCCESS,
    payload: {
      goodId: goodId,
      review: review
    }
  };
}

function submitReviewFromPurchasesSuccess(purchasedGoodsKey, review) {
  return {
    type: ActionTypes.REVIEW_SUBMITTED_FROM_PURCHASES_SUCCESS,
    payload: {
      purchasedGoodsKey: purchasedGoodsKey,
      review: review
    }
  };
}

export function submitReview(goodId, transactionId, rating, reviewText, pageOpenedFrom, parentId) {
  return function (dispatch) {
    dispatch(_submitReview());
    ProductMarketplaceClient.submitReview(goodId, transactionId, rating, reviewText).then(function (review) {
      if (pageOpenedFrom === 'details') {
        dispatch(submitReviewFromDetailsSuccess(goodId, review));
        return;
      }

      dispatch(submitReviewFromPurchasesSuccess(parentId ? generatePurchasedGoodsKeyForChildGood({
        childId: goodId,
        parentId: parentId
      }) : goodId, review));
    }, function (error) {
      dispatch(_submitReview(new Error(error)));
    }).done();
  };
}

function _submitRefund(error) {
  if (error) {
    return {
      type: ActionTypes.REFUND_SUBMITTED,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.REFUND_SUBMITTED
  };
}

function _submitRefundSuccess() {
  return {
    type: ActionTypes.REFUND_SUBMITTED_SUCCESS
  };
}

function _cancelRefundSuccess(goodId) {
  return {
    type: ActionTypes.REFUND_CANCEL_SUCCESS,
    payload: goodId
  };
}

function _updateRefund(error) {
  if (error) {
    return {
      type: ActionTypes.REFUND_UPDATED,
      payload: error,
      error: true
    };
  }

  return {
    type: ActionTypes.REFUND_UPDATED
  };
}

function _updateRefundSuccess(refund, goodId) {
  return {
    type: ActionTypes.REFUND_UPDATE_SUCCESS,
    payload: {
      refund: refund,
      goodId: goodId
    }
  };
}

export function closeRefundModal() {
  return {
    type: ActionTypes.DISMISSED_REFUND_MODAL
  };
}
export function closeUninstallConfirmModal() {
  return {
    type: ActionTypes.DISMISSED_UNINSTALL_CONFIRM_MODAL
  };
}
export function closeUninstallModal() {
  return {
    type: ActionTypes.DISMISSED_UNINSTALL_MODAL
  };
}

function showRefundModal(goodToRefund) {
  return {
    type: ActionTypes.REFUND_MODAL_OPENED,
    payload: goodToRefund
  };
}

function showUninstallModal(_ref4) {
  var modalType = _ref4.modalType,
      good = _ref4.good,
      assetsWhereGoodInUse = _ref4.assetsWhereGoodInUse,
      packChildrenInUse = _ref4.packChildrenInUse;
  return {
    type: ActionTypes.SHOW_UNINSTALL_MODAL,
    payload: {
      modalType: modalType,
      goodName: good.get('name'),
      goodSubtype: good.get('subType'),
      assetsWhereGoodInUse: assetsWhereGoodInUse,
      packChildrenInUse: packChildrenInUse
    }
  };
}

function checkPackChildUsageAndTriggerModal(goodToRefund, purchasedGoods, dispatch) {
  CosIndexClient.checkPackChildUsage(getPackChildrenDestinationIds(goodToRefund, purchasedGoods)).then(function (response) {
    var childrenInUseDestinationIds = getPackChildrenInUseDestinationIds(response);

    if (!childrenInUseDestinationIds.size) {
      dispatch(showRefundModal(goodToRefund));
      return;
    }

    dispatch(showUninstallModal({
      modalType: UninstallModalTypes.ATTEMPTED_REFUND_PACK,
      good: goodToRefund,
      assetsWhereGoodInUse: [],
      packChildrenInUse: getPackChildrenInUse(childrenInUseDestinationIds, goodToRefund, purchasedGoods)
    }));
    return;
  }, function () {
    dispatch(showRefundModal(goodToRefund));
  }).done();
}

function checkGoodUsageAndTriggerModal(goodToRefund, dispatch) {
  CosIndexClient.checkGoodUsage(goodToRefund.get('destinationId')).then(function (response) {
    if (!response.length) {
      dispatch(showRefundModal(goodToRefund));
      return;
    }

    dispatch(showUninstallModal({
      modalType: UninstallModalTypes.ATTEMPTED_REFUND_INDIVIDUAL,
      good: goodToRefund,
      assetsWhereGoodInUse: response,
      packChildrenInUse: []
    }));
    return;
  }, function () {
    dispatch(showRefundModal(goodToRefund));
  }).done();
}

function _checkGoodUsage(error, errorType, good) {
  if (error) {
    var goodId = good ? good.get('goodId') : '';
    return {
      type: ActionTypes.CHECK_GOOD_USAGE,
      payload: {
        errorId: errorType + "-" + goodId,
        errorType: errorType,
        good: good
      },
      error: true
    };
  }

  return {
    type: ActionTypes.CHECK_GOOD_USAGE
  };
}

export function triggerRefundOrUninstallModal(goodToRefund) {
  return function (dispatch, getState) {
    dispatch(_checkGoodUsage());

    if (goodToRefund.get('totalChildren')) {
      checkPackChildUsageAndTriggerModal(goodToRefund, getState().managePurchases.get('purchasedGoods'), dispatch);
      return;
    }

    checkGoodUsageAndTriggerModal(goodToRefund, dispatch);
  };
}

function showUninstallConfirmModal(goodToUninstall) {
  return {
    type: ActionTypes.SHOW_UNINSTALL_CONFIRM_MODAL,
    payload: goodToUninstall
  };
}

function triggerConfirmOrUninstallModalForTemplates(goodToUninstall, dispatch) {
  CosIndexClient.checkGoodUsage(goodToUninstall.get('destinationId')).then(function (response) {
    if (!response.length) {
      dispatch(showUninstallConfirmModal(goodToUninstall));
      return;
    }

    dispatch(showUninstallModal({
      modalType: UninstallModalTypes.ATTEMPTED_UNINSTALL_INDIVIDUAL,
      good: goodToUninstall,
      assetsWhereGoodInUse: response,
      packChildrenInUse: []
    }));
    return;
  }, function (error) {
    dispatch(_checkGoodUsage(new Error(error), CheckGoodUsageErrorTypes.ATTEMPTED_UNINSTALL, goodToUninstall));
  }).done();
}

function triggerConfirmOrUninstallModalForPacks(goodToUninstall, purchasedGoods, dispatch) {
  CosIndexClient.checkPackChildUsage(getPackChildrenDestinationIds(goodToUninstall, purchasedGoods)).then(function (response) {
    var childrenInUseDestinationIds = getPackChildrenInUseDestinationIds(response);

    if (!childrenInUseDestinationIds.size) {
      dispatch(showUninstallConfirmModal(goodToUninstall));
      return;
    }

    dispatch(showUninstallModal({
      modalType: UninstallModalTypes.ATTEMPTED_UNINSTALL_PACK,
      good: goodToUninstall,
      assetsWhereGoodInUse: [],
      packChildrenInUse: getPackChildrenInUse(childrenInUseDestinationIds, goodToUninstall, purchasedGoods)
    }));
    return;
  }, function (error) {
    dispatch(_checkGoodUsage(new Error(error), CheckGoodUsageErrorTypes.ATTEMPTED_UNINSTALL, goodToUninstall));
  }).done();
}

export function triggerConfirmOrUninstallModal(goodToUninstall) {
  return function (dispatch, getState) {
    dispatch(_checkGoodUsage());

    if (goodToUninstall.get('totalChildren')) {
      triggerConfirmOrUninstallModalForPacks(goodToUninstall, getState().managePurchases.get('purchasedGoods'), dispatch);
      return;
    }

    triggerConfirmOrUninstallModalForTemplates(goodToUninstall, dispatch);
  };
}

function goodUsageInfoReceived(good, response) {
  return {
    type: ActionTypes.GOOD_USAGE_INFO_RECEIVED,
    payload: {
      assetsWhereGoodInUse: response,
      purchasedGoodsKey: !good.get('parentId') ? good.get('goodId') : generatePurchasedGoodsKeyForChildGood({
        childId: good.get('goodId'),
        parentId: good.get('parentId')
      })
    }
  };
}

export function checkGoodUsage(good) {
  return function (dispatch) {
    CosIndexClient.checkGoodUsage(good.get('destinationId')).then(function (response) {
      dispatch(goodUsageInfoReceived(good, response));
      return;
    }, function (error) {
      dispatch(_checkGoodUsage(new Error(error), CheckGoodUsageErrorTypes.OPENED_DROPDOWN, good));
    }).done();
  };
}
export function triggerSeeWhereInUseModal(good) {
  return function (dispatch) {
    dispatch(showUninstallModal({
      modalType: UninstallModalTypes.SEE_WHERE_IN_USE,
      good: good,
      assetsWhereGoodInUse: good.get('assetsWhereGoodInUse'),
      packChildrenInUse: []
    }));
  };
}
export function submitRefund(transactionId, reason) {
  return function (dispatch) {
    dispatch(_submitRefund());
    ProductMarketplaceClient.submitRefund(transactionId, reason).then(function (response) {
      dispatch(_submitRefundSuccess());
      dispatch(receivePurchasedGoods(response));
    }, function (error) {
      dispatch(_submitRefund(error));
    }).done();
  };
}
export function updateRefund(transactionId, status, reason, goodId) {
  return function (dispatch) {
    dispatch(_updateRefund());
    ProductMarketplaceClient.updateRefund(transactionId, status, reason).then(function (response) {
      dispatch(_updateRefundSuccess(response, goodId)); // If they submitted reason, they're requesting
      // a refund after cancelling a previous one.
      // Dispatching submitRefundSuccess
      // handles the refundModal state

      if (reason) {
        dispatch(_submitRefundSuccess());
      } // If they're canceling a refund, we want to update
      // the delivery status from "REFUNDED" to "UNINSTALLED."
      // Dispatching cancelRefundSuccess handles this.


      if (status === CANCELED) {
        dispatch(_cancelRefundSuccess(goodId));
      }
    }, function (error) {
      if (reason) {
        dispatch(_submitRefund(error));
      }

      dispatch(_updateRefund(error));
    }).done();
  };
}
export function trackMultiselectSelection(adjustedFilter, selection) {
  return {
    type: ActionTypes.TRACK_MULTISELECT_SELECTION,
    payload: {
      adjustedFilter: adjustedFilter,
      selection: selection
    }
  };
}

function receivePollingGoodInfo(goodId, goodStatus, goodDeliverInfo) {
  return {
    type: ActionTypes.POLLING_GOOD_INFO_RECEIVED,
    payload: {
      goodId: goodId,
      goodStatus: goodStatus,
      goodDeliverInfo: goodDeliverInfo
    }
  };
}

function displaySlowDeliveryMessage(goodId) {
  return {
    type: ActionTypes.OPEN_SLOW_DELIVERY_MODAL,
    payload: {
      goodId: goodId
    }
  };
}

var pollGoodDeliveryStatusTimeoutId;
export function pollGoodDeliveryStatus(goodId) {
  var elapsedSeconds = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return function (dispatch) {
    if (pollGoodDeliveryStatusTimeoutId && !elapsedSeconds) {
      window.clearTimeout(pollGoodDeliveryStatusTimeoutId);
      pollGoodDeliveryStatusTimeoutId = null;
    }

    ProductMarketplaceClient.getDeliverStatus(goodId).then(function (data) {
      if (data.status === RequestStatusTypes.SUCCEEDED) {
        dispatch(receivePollingGoodInfo(goodId, RequestStatusTypes.SUCCEEDED, data.goodDeliverInfo));
      } else if (data.status === RequestStatusTypes.PENDING) {
        dispatch(receivePollingGoodInfo(goodId, RequestStatusTypes.PENDING, []));

        if (hasFifteenMinutesElapsed(elapsedSeconds)) {
          dispatch(displaySlowDeliveryMessage(goodId));
        }

        pollGoodDeliveryStatusTimeoutId = setTimeout(function () {
          return dispatch(pollGoodDeliveryStatus(goodId, getSecondsElapsedBeforeNextPolling(elapsedSeconds)));
        }, getPollIntervalInMilliseconds(elapsedSeconds));
      }
    }, function () {
      dispatch(receivePollingGoodInfo(goodId, RequestStatusTypes.FAILED));
    }).done();
  };
}
export function onClickInstallStatusModalClose() {
  return {
    type: ActionTypes.DISMISSED_INSTALL_STATUS_MODAL
  };
}
export function goToGoodDetails(goodUrlToken) {
  return function (dispatch) {
    dispatch(push(UrlPathParams.DIRECTORY_PAGE_PATH + "/" + goodUrlToken));
  };
}
export function getProviderNames() {
  return function (dispatch) {
    dispatch({
      type: ActionTypes.PROVIDER_NAMES_REQUESTED
    });
    ProductMarketplaceClient.getProviderNames().then(function (payload) {
      dispatch({
        type: ActionTypes.PROVIDER_NAMES_RECEIVED,
        payload: payload
      });
    }, function (error) {
      dispatch({
        type: ActionTypes.PROVIDER_NAMES_FAILED,
        error: true,
        payload: error
      });
    }).done();
  };
}

function _createAsset(purchasedGoodsKey, error) {
  if (error) {
    return {
      type: ActionTypes.CREATE_ASSET,
      payload: {
        error: error,
        purchasedGoodsKey: purchasedGoodsKey
      },
      error: true
    };
  }

  return {
    type: ActionTypes.CREATE_ASSET,
    payload: {
      purchasedGoodsKey: purchasedGoodsKey
    }
  };
}

export function createAsset(_ref5) {
  var subType = _ref5.subType,
      name = _ref5.name,
      templatePath = _ref5.templatePath,
      purchasedGoodsKey = _ref5.purchasedGoodsKey;
  return function (dispatch, getState) {
    var state = getState();
    var hasSitePagesAccess = getHasSitePagesAccess(state);
    dispatch(_createAsset(purchasedGoodsKey));
    var createAssetRequest;

    if (subType === GoodSubTypes.EMAIL_TEMPLATES) {
      createAssetRequest = ProductMarketplaceClient.createEmailAsset(name, templatePath);
    } else {
      createAssetRequest = ProductMarketplaceClient.createPageAsset(name, templatePath, subType === GoodSubTypes.SITE_PAGES && hasSitePagesAccess ? 'site_page' : '');
    }

    createAssetRequest.then(function (resp) {
      var editorUrl = ContentRoutes.edit(resp.id, GoodSubTypePythonFieldNames[subType]);
      window.location = editorUrl;
    }, function (error) {
      dispatch(_createAsset(purchasedGoodsKey, error));
    }).done();
  };
}
export function togglePurchasedPack(goodId) {
  return function (dispatch) {
    dispatch({
      type: ActionTypes.TOGGLE_PURCHASED_PACK,
      payload: {
        goodId: goodId
      }
    });
  };
}
export function reinstallGood(goodId) {
  var goodName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  var updatedVersion = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return function (dispatch) {
    ProductMarketplaceClient.reinstallGood(goodId).then(function () {
      dispatch(Object.assign({
        type: ActionTypes.REDELIVERING_GOOD,
        payload: {
          goodId: goodId
        }
      }, updatedVersion && {
        meta: {
          notification: {
            type: 'success',
            titleText: I18n.text('managePurchases.updatedVersion.titleText'),
            message: I18n.text('managePurchases.updatedVersion.message', {
              goodName: goodName
            })
          }
        }
      }));
    }, function (err) {
      if (err.status === 404 && err.responseJSON.message.includes('Unable to find good')) {
        dispatch({
          type: ActionTypes.REINSTALLATION_UNAVAILABLE,
          meta: {
            notification: {
              titleText: I18n.text('managePurchases.reinstallationUnavailable.titleText'),
              message: I18n.text('managePurchases.reinstallationUnavailable.message'),
              type: 'danger'
            }
          }
        });
      } else {
        dispatch({
          type: ActionTypes.ERROR_REINSTALLING_GOOD,
          payload: err
        });
      }
    }).done();
  };
}

function _uninstallGood(error) {
  if (error) {
    return {
      type: ActionTypes.UNINSTALL_GOOD,
      error: true,
      payload: error
    };
  }

  return {
    type: ActionTypes.UNINSTALL_GOOD
  };
}

export function uninstallGood(good, skipValidation) {
  return function (dispatch) {
    dispatch(_uninstallGood());
    ProductMarketplaceClient.uninstallGood(good.get(DetailsPanelItems.TRANSACTION_ID), skipValidation).then(function (purchasedGoods) {
      dispatch(receivePurchasedGoods(purchasedGoods));
    }, function (error) {
      dispatch(_uninstallGood(error));
    }).done();
  };
}
export function trackGoodInfo(trackingData) {
  return {
    type: ActionTypes.GOOD_INFO_CLICK,
    trackingData: trackingData
  };
}
export function navigateFromGoodDetailsReview(providerUrlToken, goodNameUrlToken) {
  return function (dispatch) {
    var goodDetailsPath = formatPattern(UrlPathParams.GOOD_DETAILS_PATH, {
      providerUrlToken: providerUrlToken,
      goodNameUrlToken: goodNameUrlToken
    });
    dispatch(replace(goodDetailsPath));
  };
}
export var resetGoodDetails = function resetGoodDetails() {
  return {
    type: ActionTypes.GOOD_DETAILS_RESET
  };
};